/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { object, bool } from 'prop-types';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import ScrollbarContainer from '../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainer';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    contentTop: {
        // Desktop
        margin: '0px auto',
        maxWidth: '1380px',
        color: theme.palette.black,
        // Tablet
        [theme.breakpoints.down('md')]: {
            margin: '20px 20px 30px',
        },
        // Mobile
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 10px 0',
        },
    },
    contentLeft: {
        width: '100%',
        // Tablet
        [theme.breakpoints.down('md')]: {
            flex: '1 1 auto',
            display: 'flex',
            maxWidth: '100%',
            marginRight: '20px',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down(600)]: {
            maxWidth: '100%',
        },
        marginRight: '24px',
        maxWidth: '665px',
        '@media screen and (min-width: 600px) and (max-width:1366px)': {
            width: 'auto',
        },
    },

    contentRight: {
        width: '100%',
        maxWidth: '980px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            width: 'calc(60% - 20px)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100%',
        },
        '& [class*="YotpoProductReview-alignment"]': {
            position: 'relative',
            bottom: 'unset',
            right: 'unset',
            width: 'auto',
            [theme.breakpoints.down(767)]: {
                display: 'block',
            },
        },
        '& [class*="ProductReview-alignment"]': {
            position: 'relative',
            bottom: 'unset',
            right: 'unset',
            width: 'auto',
            [theme.breakpoints.down(767)]: {
                display: 'block',
            },
        },
    },
}));

// documentation: https://1800flowersinc.atlassian.net/wiki/spaces/RP/pages/288227337/PDP+Product+Details+Page
const TwoColumnPmall = (props) => {
    const {
        components, isPassportAfterCta, isPdpPassportHidden,
    } = props;
    const isPmallProduct = useSelector(getFeatureFlag('is-pmall-pdp-template-enabled'));
    const {
        ProductImageUI,
        SmartGift,
        ProductTitle,
        GraphqlProductSkus,
        GraphqlAddonContainer,
        GCIPersonalization,
        SnipeBanner,
        GraphqlAgeVerification,
        SubscriptionProductBannerUI,
        ProductQuantity,
        GraphqlPDPPromoContainer,
        PmallSummary,
        PmallPersonalization,
        PassportBannerUI,
        PDPAddToCart,
        GoDeluxe,
        NBCUMovieBanner,
        PDPDeliveryPopupContainer,
        ProductDetails,
        SubstitutionMessage,
        PLAProductIcon,
        QandASection,
    } = components;

    const ssrDeviceType = useSelector(getSSRDeviceType);

    const variantName = 'TwoColumnPmallDetailVariant';

    const classes = useStyles();

    const desktopLayout = () => (
        <div
            className={classes.contentTop}
            data-testid="pdp-variant-twocolumnpmall"
        >
            <ScrollbarContainer
                contentContainerId="twoColumnRight"
                isFoodBrand
                type="productBase"
            >
                <div className={classes.contentLeft}>
                    {ProductImageUI()}
                    {SubstitutionMessage()}
                    {SmartGift()}
                </div>
            </ScrollbarContainer>
            <div className={classes.contentRight} id="twoColumnRight">
                {ProductTitle()}
                {isPmallProduct && ProductDetails(variantName)}
                {GraphqlProductSkus()}
                {GraphqlAddonContainer()}
                {GCIPersonalization()}
                {SnipeBanner()}
                {GraphqlAgeVerification()}
                {SubscriptionProductBannerUI()}
                {PmallSummary()}
                {GraphqlPDPPromoContainer()}
                {PmallPersonalization()}
                {!isPassportAfterCta && !isPdpPassportHidden &&  PassportBannerUI()}
                {ProductQuantity()}
                {PDPAddToCart()}
                {isPassportAfterCta && !isPdpPassportHidden &&  PassportBannerUI()}
                {!isPmallProduct && ProductDetails(variantName)}
                {NBCUMovieBanner()}
                {PDPDeliveryPopupContainer()}
                {QandASection()}
            </div>
        </div>
    );

    const mobileLayout = () => (
        <>
            {PLAProductIcon()}
            <div className={classes.contentTop}>
                <div className={classes.contentLeft}>
                    {ProductImageUI()}
                </div>
                <div className={classes.contentRight}>
                    {ProductTitle()}
                    {isPmallProduct && ProductDetails(variantName)}
                    {GraphqlProductSkus()}
                    {GoDeluxe(null, true)}
                    {GraphqlAddonContainer()}
                    {GCIPersonalization()}
                    {SnipeBanner()}
                    {GraphqlAgeVerification()}
                    {SubscriptionProductBannerUI()}
                    {PmallSummary()}
                    {GraphqlPDPPromoContainer()}
                    {PmallPersonalization()}
                    {!isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                    {ProductQuantity()}
                    {PDPAddToCart()}
                    {isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                    {!isPmallProduct && ProductDetails(variantName)}
                    {NBCUMovieBanner()}
                    {PDPDeliveryPopupContainer()}
                    {SmartGift()}
                    {SubstitutionMessage()}
                    {GoDeluxe()}
                    {QandASection()}
                </div>
            </div>
        </>
    );

    if (ssrDeviceType === 'mobile') {
        return mobileLayout();
    }

    return desktopLayout();
};

TwoColumnPmall.propTypes = {
    components: object.isRequired,
    isPassportAfterCta: bool,
    isPdpPassportHidden: bool,
};

TwoColumnPmall.defalutProps = {
    isPassportAfterCta: false,
    isPdpPassportHidden: false,
};
export default TwoColumnPmall;
